import { ADDRESS, BOOKING_URL, CLASSPASS_URL, GBP_DIRECTIONS_URL, GBP_REVIEWS_URL, GBP_URL, IMG, INSTAGRAM_URL, MAP_EMBED, NEIGHBORHOODS, PARENT_SITE, PHONE_NUMBER, PHONE_TEL, SOCIAL_HANDLE, TIKTOK_URL } from '../../lib/constants';
import { Phone, MapPin, Mail, Instagram, Star, Navigation as NavIcon, ExternalLink } from 'lucide-react';
import { TikTokIcon } from './SocialFollow';

export const Footer = () => {
  return (
    <footer
      id="location"
      data-testid="site-footer"
      className="bg-brand-bg text-brand-cream pt-0 pb-10 border-t border-brand-gold/15"
    >
      {/* Storefront banner — full-width image strip right before the final CTA / map */}
      <div className="relative w-full overflow-hidden border-b border-brand-gold/15" data-testid="footer-storefront-banner">
        <div className="container-prep py-10 sm:py-24">
          <div className="relative aspect-[4/3] sm:aspect-[21/9] lg:aspect-[21/7] rounded-2xl sm:rounded-3xl overflow-hidden border border-brand-gold/25 shadow-[0_30px_80px_-30px_rgba(201,169,110,0.4)]">
            <img
              src={IMG.storefront}
              alt="Prép Beauty Parlour storefront on Burrard St — best YUMI lash lift in Vancouver"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/85 via-brand-bg/10 to-transparent" />
            <div className="absolute bottom-5 left-5 sm:bottom-10 sm:left-10 max-w-md pr-5">
              <p className="eyebrow mb-2 sm:mb-3">Find Us</p>
              <p className="font-serif text-2xl sm:text-4xl lg:text-5xl text-brand-cream leading-tight">
                1305 Burrard St
                <span className="block italic text-brand-gold">Downtown Vancouver</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-prep pt-16 sm:pt-20">
        {/* Final CTA + map */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20 border-b border-brand-gold/15">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">Visit Us</p>
            <h2 className="heading-2 mb-6">
              Ready for effortlessly
              <span className="italic block text-brand-gold">lifted lashes?</span>
            </h2>
            <p className="text-brand-muted max-w-xl mb-8">
              Book your YUMI or InLei lash lift at Prép Beauty Parlour —
              1305 Burrard St, Downtown Vancouver. Available 7 days a week.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-book-button"
                className="btn-primary"
              >
                Book My Lash Lift →
              </a>
              <a
                href={PHONE_TEL}
                data-testid="footer-phone-button"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-brand-cream/25 text-brand-cream hover:bg-brand-cream hover:text-brand-ink transition-all text-sm font-medium"
              >
                <Phone size={15} /> {PHONE_NUMBER}
              </a>
              <a
                href={GBP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-gbp-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-brand-gold/30 text-brand-cream hover:bg-brand-gold/10 hover:border-brand-gold transition-all text-sm font-medium"
              >
                <Star size={14} className="fill-brand-gold text-brand-gold" /> View on Google
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            {/* Google rating card — pulled from the GBP knowledge graph */}
            <a
              href={GBP_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-gbp-rating"
              className="flex items-center justify-between gap-4 bg-brand-bgElev border border-brand-gold/25 rounded-2xl px-5 py-4 mb-4 hover:border-brand-gold/60 hover:bg-brand-bgSoft transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center font-bold text-brand-ink text-lg">
                  G
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-serif text-2xl text-brand-cream leading-none">5.0</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={12} className="fill-brand-gold text-brand-gold" />
                      ))}
                    </div>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1">
                    400+ Google Reviews
                  </div>
                </div>
              </div>
              <ExternalLink size={16} className="text-brand-muted group-hover:text-brand-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </a>

            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-brand-gold/20">
              <iframe
                src={MAP_EMBED}
                title="Prép Beauty Parlour map — 1305 Burrard St, Vancouver"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map"
              />
            </div>
            <a
              href={GBP_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-directions"
              className="mt-3 inline-flex items-center gap-2 text-sm text-brand-cream/85 hover:text-brand-gold transition-colors"
            >
              <NavIcon size={14} className="text-brand-gold" />
              Get directions on Google Maps →
            </a>
          </div>
        </div>

        {/* Lower grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 py-16">
          <div className="col-span-2">
            <div className="font-serif text-3xl mb-4">
              <span className="italic">YUMI Lash Lift</span>{' '}
              <span className="text-brand-gold">Vancouver</span>
            </div>
            <p className="text-brand-muted text-sm leading-relaxed max-w-sm">
              The dedicated YUMI lash lift microsite for Prép Beauty Parlour — Vancouver's most-reviewed lash studio. Authentic YUMI Keratin and InLei Lash Filler® treatments by certified artists.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label={`Instagram ${SOCIAL_HANDLE}`} data-testid="footer-instagram" className="w-9 h-9 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-cream hover:bg-brand-gold hover:text-brand-ink hover:border-brand-gold transition">
                <Instagram size={15} />
              </a>
              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" aria-label={`TikTok ${SOCIAL_HANDLE}`} data-testid="footer-tiktok" className="w-9 h-9 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-cream hover:bg-brand-gold hover:text-brand-ink hover:border-brand-gold transition">
                <TikTokIcon size={15} />
              </a>
              <a href={`mailto:info@prepbeautyparlour.com`} aria-label="Email" className="w-9 h-9 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-cream hover:bg-brand-gold hover:text-brand-ink hover:border-brand-gold transition">
                <Mail size={15} />
              </a>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-brand-muted">
              <li className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0 text-brand-gold" />{ADDRESS}</li>
              <li className="flex items-start gap-2"><Phone size={14} className="mt-0.5 shrink-0 text-brand-gold" /><a href={PHONE_TEL} className="hover:text-brand-gold">{PHONE_NUMBER}</a></li>
              <li className="pt-2 text-brand-muted/70 text-xs uppercase tracking-wider">Mon–Fri · 10am–7pm</li>
              <li className="text-brand-muted/70 text-xs uppercase tracking-wider">Sat · 10am–6pm · Sun · 11am–6pm</li>
            </ul>
          </div>

          <div>
            <div className="eyebrow mb-4">Areas Served</div>
            <ul className="space-y-2 text-sm text-brand-muted">
              {NEIGHBORHOODS.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Local SEO paragraph */}
        <div className="py-8 border-t border-brand-gold/15 text-sm text-brand-muted leading-relaxed">
          Serving lash lift clients from the West End, Yaletown, Davie Village, Kitsilano, Coal Harbour, and across Vancouver. Find us at 1305 Burrard St, Vancouver, BC — steps from Burrard SkyTrain Station. Book your lash lift online or call <a href={PHONE_TEL} className="text-brand-cream hover:text-brand-gold">{PHONE_NUMBER}</a>. Looking for the full Prép Beauty Parlour experience? Visit our <a href={PARENT_SITE} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-gold underline-offset-4 text-brand-cream hover:text-brand-gold">main lash lift page →</a>
        </div>

        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-brand-muted/70">
          <div>© {new Date().getFullYear()} Prép Beauty Parlour. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <a
              href={CLASSPASS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-classpass"
              className="hover:text-brand-gold transition-colors"
            >
              Also on ClassPass
            </a>
            <a href="#top" className="hover:text-brand-gold">Back to top ↑</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
